import {
  awaitingSignaturesResponseSchema,
  cancelSigningResponseSchema,
  commonErrorCodeResponseSchema,
  createSigningRequestSchema,
  createSigningResponseSchema,
  deleteSigningResponseSchema,
  getSignersResponseSchema,
  getSigningAuditTrailResponseSchema,
  getSigningResponseSchema,
  listSigningsResponseSchema,
  permissionDeniedResponseSchema,
  sendSignerReminderResponseSchema,
  signerCannotReceiveReminderResponseSchema,
  signerNotFoundResponseSchema,
  signingNotFoundResponseSchema,
  updateSigningRequestSchema,
  updateSigningResponseSchema,
} from 'xosign-types';

import { z } from 'zod';
import { API_URL_BASE } from '@/config';
import { type EndpointDefinition } from '@/types/endpointTypes';

// Extend request objects only for frontend with necessary path params
const CRUD = {
  c: createSigningRequestSchema,
  r: z.object({ workflowId: z.string() }),
  u: updateSigningRequestSchema.extend({ workflowId: z.string() }),
  d: z.object({ workflowId: z.string() }),
  remind: z.object({ workflowId: z.string(), signerId: z.string() }),
};

const endpoints = {
  'signings.awaiting': {
    method: 'GET',
    url: `${API_URL_BASE}signings/my`,
    requestSchema: z.undefined(),
    responseSuccessSchema: awaitingSignaturesResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: z.undefined(),
  } satisfies EndpointDefinition<
    z.ZodUndefined,
    typeof awaitingSignaturesResponseSchema,
    z.ZodUndefined
  >,
  'signers.get': {
    method: 'GET',
    url: `${API_URL_BASE}signings/:workflowId/signers`,
    requestSchema: CRUD.r,
    responseSuccessSchema: getSignersResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: z.undefined(),
  } satisfies EndpointDefinition<
    typeof CRUD.r,
    typeof getSignersResponseSchema,
    z.ZodUndefined
  >,
  'signers.remind': {
    method: 'POST',
    url: `${API_URL_BASE}signings/:workflowId/signers/:signerId/reminder`,
    requestSchema: CRUD.remind,
    responseSuccessSchema: sendSignerReminderResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      400: signerCannotReceiveReminderResponseSchema,
      403: permissionDeniedResponseSchema,
      404: signingNotFoundResponseSchema,
      422: signerNotFoundResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof CRUD.remind,
    typeof sendSignerReminderResponseSchema,
    {
      400: typeof signerCannotReceiveReminderResponseSchema;
      403: typeof permissionDeniedResponseSchema;
      404: typeof signingNotFoundResponseSchema;
      422: typeof signerNotFoundResponseSchema;
    }
  >,
  'signings.create': {
    method: 'POST',
    url: `${API_URL_BASE}signings`,
    requestSchema: CRUD.c,
    responseSuccessSchema: createSigningResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      400: commonErrorCodeResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof CRUD.c,
    typeof createSigningResponseSchema,
    { 400: typeof commonErrorCodeResponseSchema }
  >,
  'signings.delete': {
    method: 'DELETE',
    url: `${API_URL_BASE}signings/:workflowId`,
    requestSchema: CRUD.d,
    responseSuccessSchema: deleteSigningResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: z.undefined(),
  } satisfies EndpointDefinition<
    typeof CRUD.d,
    typeof deleteSigningResponseSchema,
    z.ZodUndefined
  >,
  'signings.get': {
    method: 'GET',
    url: `${API_URL_BASE}signings/:workflowId`,
    requestSchema: CRUD.r,
    responseSuccessSchema: getSigningResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: z.undefined(),
  } satisfies EndpointDefinition<
    typeof CRUD.r,
    typeof getSigningResponseSchema,
    z.ZodUndefined
  >,
  'signings.get.all': {
    method: 'GET',
    url: `${API_URL_BASE}signings`,
    requestSchema: z.undefined(),
    responseSuccessSchema: listSigningsResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: z.undefined(),
  } satisfies EndpointDefinition<
    z.ZodUndefined,
    typeof listSigningsResponseSchema,
    z.ZodUndefined
  >,
  'signings.get.auditTrail': {
    method: 'GET',
    url: `${API_URL_BASE}signings/:workflowId/audit-trail`,
    requestSchema: CRUD.r,
    responseSuccessSchema: getSigningAuditTrailResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: z.undefined(),
  } satisfies EndpointDefinition<
    typeof CRUD.r,
    typeof getSigningAuditTrailResponseSchema,
    z.ZodUndefined
  >,
  'signings.cancel': {
    method: 'POST',
    url: `${API_URL_BASE}signings/:workflowId/cancel`,
    requestSchema: CRUD.r,
    responseSuccessSchema: cancelSigningResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: z.undefined(),
  } satisfies EndpointDefinition<
    typeof CRUD.r,
    typeof cancelSigningResponseSchema,
    z.ZodUndefined
  >,
  'signings.start': {
    method: 'POST',
    url: `${API_URL_BASE}signings/:workflowId/start`,
    requestSchema: CRUD.r,
    responseSuccessSchema: updateSigningResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      400: commonErrorCodeResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof CRUD.r,
    typeof updateSigningResponseSchema,
    { 400: typeof commonErrorCodeResponseSchema }
  >,
  'signings.update': {
    method: 'PUT',
    url: `${API_URL_BASE}signings/:workflowId`,
    requestSchema: CRUD.u,
    responseSuccessSchema: updateSigningResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      400: commonErrorCodeResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof CRUD.u,
    typeof updateSigningResponseSchema,
    { 400: typeof commonErrorCodeResponseSchema }
  >,
  'signings.detect': {
    method: 'POST',
    url: `${API_URL_BASE}signings/:workflowId/detect`,
    requestSchema: CRUD.r,
    responseSuccessSchema: z.object({
      success: z.literal(true),
      data: z.object({
        jobs: z.array(
          z.object({
            documentId: z.string(),
            jobId: z.string(),
            pageDimensions: z.array(
              z.object({ width: z.number(), height: z.number() })
            ),
          })
        ),
      }),
    }),
    requiresToken: 'local',
    responseErrorSchemas: z.undefined(),
  } satisfies EndpointDefinition<
    typeof CRUD.r,
    z.ZodObject<{
      success: z.ZodLiteral<true>;
      data: z.ZodObject<{
        jobs: z.ZodArray<
          z.ZodObject<{
            documentId: z.ZodString;
            jobId: z.ZodString;
            pageDimensions: z.ZodArray<
              z.ZodObject<{ width: z.ZodNumber; height: z.ZodNumber }>
            >;
          }>
        >;
      }>;
    }>,
    z.ZodUndefined
  >,
  'signings.summary': {
    method: 'POST',
    url: `${API_URL_BASE}signings/:workflowId/summary`,
    requestSchema: CRUD.r,
    responseSuccessSchema: z.object({
      success: z.literal(true),
      data: z.object({ jobId: z.string() }),
    }),
    requiresToken: 'local',
    responseErrorSchemas: z.undefined(),
  } satisfies EndpointDefinition<
    typeof CRUD.r,
    z.ZodObject<{
      success: z.ZodLiteral<true>;
      data: z.ZodObject<{ jobId: z.ZodString }>;
    }>,
    z.ZodUndefined
  >,
  'signings.summary.status': {
    method: 'GET',
    url: `${API_URL_BASE}signings/:workflowId/summary/:jobId`,
    requestSchema: z.object({ workflowId: z.string(), jobId: z.string() }),
    responseSuccessSchema: z.object({
      success: z.literal(true),
      status: z.enum(['pending', 'processing', 'done', 'failed']),
      progress: z.number().optional(),
      result: z
        .object({
          pageCount: z.number(),
          summary: z.string(),
        })
        .optional(),
      error: z.string().optional(),
    }),
    requiresToken: 'local',
    responseErrorSchemas: z.undefined(),
  } satisfies EndpointDefinition<
    z.ZodObject<{ workflowId: z.ZodString; jobId: z.ZodString }>,
    z.ZodObject<any>,
    z.ZodUndefined
  >,
  'signings.detect.status': {
    method: 'GET',
    url: `${API_URL_BASE}signings/:workflowId/detect/:jobId`,
    requestSchema: z.object({ workflowId: z.string(), jobId: z.string() }),
    responseSuccessSchema: z.object({
      success: z.literal(true),
      status: z.enum(['pending', 'processing', 'done', 'failed']),
      progress: z.number().optional(),
      result: z
        .object({
          pageCount: z.number(),
          fields: z.array(
            z.object({
              signerId: z.string(),
              page: z.number(),
              x: z.number(),
              y: z.number(),
              pt: z.object({ x: z.number(), y: z.number() }),
              confidence: z.enum(['high', 'inferred']),
              matchedText: z.string().optional(),
            })
          ),
        })
        .optional(),
      partialFields: z
        .array(
          z.object({
            signerId: z.string(),
            page: z.number(),
            x: z.number(),
            y: z.number(),
            pt: z.object({ x: z.number(), y: z.number() }),
            confidence: z.enum(['high', 'inferred']),
            matchedText: z.string().optional(),
          })
        )
        .optional(),
      error: z.string().optional(),
    }),
    requiresToken: 'local',
    responseErrorSchemas: z.undefined(),
  } satisfies EndpointDefinition<
    z.ZodObject<{ workflowId: z.ZodString; jobId: z.ZodString }>,
    z.ZodObject<any>,
    z.ZodUndefined
  >,
};

export default endpoints;
